import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import { interpolate, reverseInterpolate } from 'modules/OldSliders/SalePriceSlider/utils'
import SliderInput from 'modules/Sliders/SliderInput'

import Container from 'components/GlobalStyles/Marketing/Container'
import RoundedButton from 'components/RoundedButton'
import FlaredArrow from 'components/SVG/FlaredArrow'
import MinusSquared from 'components/SVG/MinusSquared'
import PlusSquared from 'components/SVG/PlusSquared'
import Spacer from 'components/Spacer'

import { addCommas } from 'utils/stringFormatting'

const WhiteBackground = styled.div`
  background-color: ${props => props.theme.colors.white};
`

const HeaderText = styled.div`
  font-size: 24px;
  font-weight: 400;
  text-align: center;
  width: 100%;
  color: ${props => props.theme.colors.grey27};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 32px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 42px;
  }
`

const Br = styled.br`
  display: none;
  @media (max-width: ${props => props.theme.metrics.phone}px) {
    display: block;
  }
`

const Bold = styled.span`
  font-weight: 800;
`

const KeepValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-direction: row;
  }
`

const KeepValueText = styled.span`
  text-align: center;
  font-size: 16px;
  color: ${props => props.theme.colors.grey25};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 24px;
  }
`

const StyledSpan = styled.span`
  font-weight: 900;
  font-size: 22px;
  color: ${props => props.theme.colors.yellow4};

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 36px;
  }
`
interface IProps {
  defaultPrice?: number
  sliderMin?: number
  sliderMax?: number
  onChange?: (price: number) => void
  scrollTo?: () => void
}

const SalePriceSlider: FunctionComponent<IProps> = ({
  defaultPrice = 320000,
  sliderMin = 0,
  sliderMax = 150,
  onChange,
  scrollTo,
}) => {
  const theme = useContext(ThemeContext)

  const renderRightArrowIcon = () => (
    <FlaredArrow
      fill={theme.colors.white}
      width={15}
      height={15}
    />
  )

  const defaultTickValue = (price: number) => reverseInterpolate(price)

  // slider state
  const [value, setValue] = useState(defaultTickValue(defaultPrice))

  const handleSliderChange = (_: string, newValue: number) => {
    if (onChange) onChange(interpolate(newValue))

    setValue(newValue)
  }

  useEffect(() => {
    setValue(defaultTickValue(defaultPrice))
  }, [defaultPrice])

  // formulas to compute savings
  const selectedPrice = interpolate(value)
  const brokerSavings = addCommas(Math.floor(selectedPrice * 0.035 - 599).toString())
  const noBrokerSavings = addCommas(Math.floor(selectedPrice * 0.06 - 599).toString())

  return (
    <WhiteBackground>
      <Container>
        <Spacer
          size={72}
          tabletSize={48}
          responsiveSize={32}
        />
        <HeaderText>
          <Bold>It&apos;s your equity.</Bold> <Br />
          <Bold>Keep more of it.</Bold>{' '}
        </HeaderText>
        {/* New Sale Price Slider */}
        <SliderInput
          max={sliderMax}
          min={sliderMin}
          step={1}
          name="sale-price"
          value={value}
          marginTop={160}
          onChange={handleSliderChange}
          skipDebounce
          popoverLabel="My Home Value"
          popoverValue={`$${addCommas(interpolate(value).toFixed(0))}`}
          sliderLeftIcon={(
            <MinusSquared
              width={32}
              height={32}
              fill={theme.colors.blue}
            />
          )}
          sliderRightIcon={(
            <PlusSquared
              width={32}
              height={32}
              fill={theme.colors.blue4}
            />
          )}
        />
        <Spacer
          size={50}
          tabletSize={36}
          responsiveSize={36}
        />
        <KeepValueContainer>
          <KeepValueText>Your Commission Savings:</KeepValueText>
          <Spacer size={10} />
          <StyledSpan>{` $${brokerSavings} to $${noBrokerSavings}`}</StyledSpan>
        </KeepValueContainer>
        <Spacer
          size={50}
          tabletSize={36}
          responsiveSize={36}
        />
        <RoundedButton
          text="Get Started"
          background={theme.colors.blue4}
          hoverBackground={theme.colors.blue9}
          color={theme.colors.white}
          boxShadow={`0px 12px 16px ${theme.colors.transparentBlue4Alpha25}`}
          renderRightIcon={renderRightArrowIcon}
          fontSize={12}
          radius={50}
          height={44}
          width={240}
          center
          onClick={scrollTo}
        />
        <Spacer
          size={72}
          tabletSize={48}
          responsiveSize={32}
        />
      </Container>
    </WhiteBackground>
  )
}

export default SalePriceSlider
